"use client";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";

interface CapabilityAutopilotRowProps {
  tasks: string[];
}

const VISIBLE = 3;

const CapabilityAutopilotRow = ({ tasks }: CapabilityAutopilotRowProps) => {
  const [head, setHead] = useState(0); // absolute id of the top row
  const [ticked, setTicked] = useState(false); // top row switched on → tick

  useEffect(() => {
    const t1 = setTimeout(() => setTicked(true), 1300);
    const t2 = setTimeout(() => {
      setTicked(false);
      setHead((h) => h + 1);
    }, 2300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [head]);

  const rows = Array.from({ length: VISIBLE }, (_, k) => head + k);

  return (
    <div className="mt-auto pt-6">
      <div className="space-y-2.5">
        <AnimatePresence initial={false} mode="popLayout">
          {rows.map((id, k) => {
            const isTop = k === 0;
            return (
              <motion.div
                key={id}
                layout
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -22 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white px-3 py-2.5"
              >
                <motion.p
                  className="flex-1 text-xs font-semibold"
                  animate={{ color: isTop ? "#404040" : "#a3a3a3" }}
                  transition={{ duration: 0.5 }}
                >
                  {tasks[id % tasks.length]}
                </motion.p>
                <AnimatePresence mode="wait" initial={false}>
                  {isTop && ticked ? (
                    <motion.span
                      key="check"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      transition={{ duration: 0.25 }}
                      className="grid size-5 shrink-0 place-items-center rounded-full bg-card-foreground text-white"
                    >
                      <Check size={12} />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="toggle"
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: 1,
                        backgroundColor: isTop ? "#212129" : "#e5e5e5",
                      }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.8 }}
                      className="flex h-4 w-7 shrink-0 items-center rounded-full px-0.5"
                    >
                      <motion.span
                        className="size-3 rounded-full bg-white"
                        animate={{ x: isTop ? 12 : 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                      />
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CapabilityAutopilotRow;
